import { useMemo } from 'react';
import { ErrorState } from '@/components/states';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskText, type DeskTextSource } from '@/lib/desk-files';
import { t } from '@/lib/i18n';
import { diffLines, diffParts } from '@/lib/line-diff';
import { cn } from '@/lib/utils';

/**
 * What changed in a desk file's text, line by line: the content before (null: there was no file) against the one
 * after (null: deleted), each by its git id or as a path in a commit. On a phone only the new line numbers show.
 */
export function TextDiff({
  deskId,
  before,
  after,
  className,
}: {
  deskId: string;
  before: DeskTextSource | null;
  after: DeskTextSource | null;
  className?: string;
}) {
  const a = useDeskText(deskId, before);
  const b = useDeskText(deskId, after);
  const ready = (!before || a.isSuccess) && (!after || b.isSuccess);
  const binary = (before && a.data === null) || (after && b.data === null);
  const hasBefore = !!before;
  const hasAfter = !!after;
  const lines = useMemo(
    () => (ready && !binary ? diffLines(hasBefore ? (a.data ?? '') : '', hasAfter ? (b.data ?? '') : '') : null),
    [ready, binary, hasBefore, hasAfter, a.data, b.data],
  );
  const parts = useMemo(() => (lines ? diffParts(lines) : null), [lines]);

  if (a.isError || b.isError) return <ErrorState error={a.error ?? b.error} onRetry={() => void (a.isError ? a.refetch() : b.refetch())} className={className} />;
  if (!ready) return <Skeleton className={cn('h-24 w-full', className)} />;
  if (binary) return <p className={cn('text-xs text-muted-foreground', className)}>{t('Not a text file — no line-by-line comparison.')}</p>;
  if (!parts) return <p className={cn('text-xs text-muted-foreground', className)}>{t('Too many changed lines to compare here. Download both versions to compare them.')}</p>;
  if (!parts.some((p) => p.kind === 'lines')) return <p className={cn('text-xs text-muted-foreground', className)}>{t('Same text in both versions.')}</p>;

  return (
    <div className={cn('max-h-[28rem] overflow-auto rounded-md border bg-muted/20 font-mono text-xs leading-5', className)}>
      <table className="w-full border-collapse">
        <tbody>
          {parts.map((part, i) =>
            part.kind === 'skip' ? (
              <tr key={i} className="bg-muted/50 text-muted-foreground">
                <td colSpan={3} className="px-2 py-0.5 text-center font-sans text-[11px]">
                  {part.count === 1 ? t('1 unchanged line') : t('{n} unchanged lines', { n: part.count })}
                </td>
              </tr>
            ) : (
              part.lines.map((line, k) => (
                <tr
                  key={`${i}-${k}`}
                  className={cn(
                    line.kind === 'add' && 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-200',
                    line.kind === 'del' && 'bg-red-500/10 text-red-900 dark:text-red-200',
                  )}
                >
                  <td className="w-px px-1.5 text-right text-muted-foreground/70 tabular-nums select-none max-sm:hidden">{line.a ?? ''}</td>
                  <td className="w-px px-1.5 text-right text-muted-foreground/70 tabular-nums select-none">{line.b ?? line.a ?? ''}</td>
                  <td className="px-2 whitespace-pre">
                    <span className="mr-2 select-none text-muted-foreground">{line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ' '}</span>
                    {line.text}
                  </td>
                </tr>
              ))
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}
